import React, { useState } from "react";
import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Label,
} from "@headlessui/react";
import { ImageI } from "../../../types";
import { useGetImages } from "../../../api/queries";
import Spinner from "../../../componentLibrary/Spinner";
import { ImageTypeEnum } from "../../../types";

const IMAGE_TYPE_CONFIG: Record<
  ImageTypeEnum,
  {
    label: string;
    placeholder: string;
    previewClass: string;
    optionImageClass: string;
  }
> = {
  [ImageTypeEnum.ICON]: {
    label: "Icon",
    placeholder: "Select an icon...",
    previewClass: "h-16 w-16 bg-gray-100 border-2 border-dashed",
    optionImageClass: "h-6 w-6 flex-shrink-0",
  },
  [ImageTypeEnum.ILLUSTRATION]: {
    label: "Illustration",
    placeholder: "Select an illustration...",
    previewClass:
      "h-16 w-24 object-cover bg-gray-100 border-2 border-dashed rounded",
    optionImageClass: "h-8 w-12 object-cover flex-shrink-0 rounded",
  },
  [ImageTypeEnum.THUMBNAIL]: {
    label: "Thumbnail",
    placeholder: "Select a thumbnail...",
    previewClass:
      "h-16 w-24 object-cover bg-gray-100 border-2 border-dashed rounded",
    optionImageClass: "h-8 w-12 object-cover flex-shrink-0 rounded",
  },
};

export interface ImageSelectProps {
  imageType: ImageTypeEnum;
  value: ImageI | null | undefined;
  onChange: (image: ImageI | null) => void;
  disabled?: boolean;
  labelText?: string;
  /** When true, shows a "— No selection —" option and allows clearing (e.g. for optional thumbnail). */
  allowClear?: boolean;
}

export default function ImageSelect({
  imageType,
  value,
  onChange,
  disabled = false,
  labelText,
  allowClear = false,
}: ImageSelectProps) {
  const { data: images, isError, isLoading } = useGetImages(imageType);
  const [query, setQuery] = useState("");

  const config = IMAGE_TYPE_CONFIG[imageType];
  const label = labelText ?? config.label;
  const placeholder = config.placeholder;

  if (isLoading) return <Spinner className="mx-auto" />;
  if (isError || !images) {
    return (
      <div>
        {imageType === ImageTypeEnum.THUMBNAIL
          ? "Error loading thumbnails"
          : "Error loading images"}
      </div>
    );
  }

  const filtered =
    query === ""
      ? images
      : images.filter((img) =>
          img.name.toLowerCase().includes(query.toLowerCase()),
        );

  return (
    <div className="flex flex-row justify-between items-center">
      <div className="w-2/3 relative">
        <Combobox
          immediate
          value={value ?? null}
          onChange={(val) => onChange(val)}
        >
          <Label className="block text-sm font-medium text-gray-700">
            {label}
          </Label>
          <ComboboxInput
            aria-label={label}
            displayValue={(img: ImageI | null) => img?.name || ""}
            onChange={(event) => setQuery(event.target.value)}
            autoComplete="off"
            placeholder={placeholder}
            className="w-full px-4 py-2 border border-[#0F172A] rounded-md focus:outline-none focus:border-[#00A3E0] disabled:cursor-not-allowed"
            disabled={disabled}
          />
          <ComboboxOptions className="absolute mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm z-40">
            {allowClear && !disabled && (
              <ComboboxOption
                value={null}
                className={({ active }) =>
                  `relative cursor-default select-none py-2 pl-10 pr-4 text-gray-500 italic ${
                    active ? "bg-blue-100" : ""
                  }`
                }
              >
                — No {config.label.toLowerCase()} —
              </ComboboxOption>
            )}
            {filtered.length === 0 && query !== "" ? (
              <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                No results found
              </div>
            ) : (
              filtered.map((option) => (
                <ComboboxOption
                  key={option._id}
                  value={option}
                  className={({ active }) =>
                    `relative cursor-default select-none py-2 pl-10 pr-4 ${
                      active ? "bg-blue-100" : ""
                    }`
                  }
                >
                  {({ selected }) => (
                    <div className="flex items-center">
                      <img
                        src={option.src}
                        alt={option.alt}
                        className={config.optionImageClass}
                      />
                      <span
                        className={`ml-3 block truncate ${
                          selected ? "font-medium" : "font-normal"
                        }`}
                      >
                        {option.name}
                      </span>
                    </div>
                  )}
                </ComboboxOption>
              ))
            )}
          </ComboboxOptions>
        </Combobox>
      </div>
      {value?.src ? (
        <img src={value.src} alt={value.alt} className={config.previewClass} />
      ) : (
        <div
          className={`${config.previewClass} flex items-center justify-center text-xs text-gray-400`}
        >
          No {config.label.toLowerCase()}
        </div>
      )}
    </div>
  );
}
